// Custom overlay scrollbar.
//
// index.css hides native scrollbars on fine-pointer devices so they never consume
// layout width. This draws the replacement: one shared thumb, positioned over
// whichever container is currently being scrolled or hovered, fading out when idle.
// One element rather than one per container — only a single scroller can be under
// the pointer or mid-scroll at a time, so the rest would be invisible anyway.
//
// Ceiling: vertical only. Every horizontal scroller in this app is a .no-scrollbar
// chip rail that deliberately shows no bar. Add a horizontal thumb when one isn't.

// Kept in sync with the hide rule in index.css — a scroller we draw for but don't
// hide would show two bars, and one we hide but don't draw for would show none.
const SELECTOR = ".overflow-y-auto, .overflow-auto, .overflow-y-scroll";
const TRACK_INSET = 14; // clears the 14px panel corner radius, as the old track margin did
const MIN_THUMB = 28;
const HIDE_DELAY = 900;
const EDGE_OFFSET = 9; // distance from the container's right edge to the thumb's left edge

/** What a scroll container looks like right now, measured by the caller. */
interface ScrollMetrics {
  top: number;
  height: number;
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}

/** Where to draw the thumb, and the two spans a drag maps between. */
interface ThumbGeometry {
  top: number;
  height: number;
  travel: number;
  scrollRange: number;
}

/**
 * Thumb geometry for a scroll container, or null when there is nothing to draw.
 * Pure and layout-free so it can be tested without a real browser.
 */
export function thumbGeometry(
  { top, height, scrollTop, scrollHeight, clientHeight }: ScrollMetrics
): ThumbGeometry | null {
  const trackHeight = height - TRACK_INSET * 2;
  const scrollRange = scrollHeight - clientHeight;
  if (scrollRange < 1 || trackHeight < MIN_THUMB) return null;

  const thumbHeight = Math.max(MIN_THUMB, trackHeight * (clientHeight / scrollHeight));
  const travel = trackHeight - thumbHeight;
  // clamp: rubber-band overscroll pushes scrollTop outside [0, scrollRange]
  const progress = Math.min(1, Math.max(0, scrollTop / scrollRange));
  return { top: top + TRACK_INSET + progress * travel, height: thumbHeight, travel, scrollRange };
}

export function initOverlayScrollbar(): void {
  if (typeof window === "undefined") return;
  // Touch platforms keep their native overlay bars: already zero-width, and no hover.
  if (!window.matchMedia("(pointer: fine)").matches) return;

  const thumb = document.createElement("div");
  thumb.className = "overlay-scrollbar-thumb";
  document.body.appendChild(thumb);

  let target: Element | null = null;
  let hovered: Element | null = null;
  let overThumb = false;
  let drag: (ThumbGeometry & { pointerY: number; scrollTop: number }) | null = null;
  // @types/node is in scope, so `setTimeout` here is typed as Node's, not the
  // DOM's number. ReturnType keeps the annotation true whichever one wins.
  let hideTimer: ReturnType<typeof setTimeout> | 0 = 0;
  let frame = 0;

  const isScroller = (el: EventTarget | null): el is Element =>
    el instanceof Element &&
    el.matches(SELECTOR) &&
    !el.classList.contains("no-scrollbar") &&
    el.scrollHeight - el.clientHeight > 1;

  function layout(): ThumbGeometry | null {
    if (!target || !target.isConnected) return null;
    const rect = target.getBoundingClientRect();
    const geo = thumbGeometry({
      top: rect.top,
      height: rect.height,
      scrollTop: target.scrollTop,
      scrollHeight: target.scrollHeight,
      clientHeight: target.clientHeight,
    });
    if (!geo) return null;
    thumb.style.top = `${geo.top}px`;
    thumb.style.height = `${geo.height}px`;
    thumb.style.left = `${rect.right - EDGE_OFFSET}px`;
    return geo;
  }

  function hide() {
    thumb.dataset.visible = "false";
    cancelAnimationFrame(frame);
    frame = 0;
  }

  // A container can be unmounted or moved with no scroll or pointer event to announce
  // it — closing a dialog, a sheet sliding out, a panel resizing. Without this the
  // thumb keeps hanging at the dead panel's coordinates until some unrelated pointer
  // event happens to re-arm the hide timer, which reads as a bar floating in mid-air.
  function track() {
    frame = 0;
    if (thumb.dataset.visible !== "true") return;
    if (!layout()) return hide();
    frame = requestAnimationFrame(track);
  }

  function scheduleHide() {
    clearTimeout(hideTimer);
    // Stay put while the pointer rests on the scroller or the thumb, matching the
    // old hover-to-reveal behaviour; otherwise fade like a native overlay bar.
    hideTimer = setTimeout(() => {
      if (!drag && !overThumb && hovered !== target) hide();
    }, HIDE_DELAY);
  }

  function show(el: Element) {
    target = el;
    if (!layout()) return hide();
    thumb.dataset.visible = "true";
    if (!frame) frame = requestAnimationFrame(track);
    scheduleHide();
  }

  // Scroll does not bubble, so listen in the capture phase to catch every container.
  document.addEventListener("scroll", (e) => {
    if (isScroller(e.target)) show(e.target);
  }, true);

  document.addEventListener("pointerover", (e) => {
    if (drag) return;
    // closest() picks the innermost scroller, which is the one the wheel will drive.
    const el = e.target instanceof Element ? e.target.closest(SELECTOR) : null;
    hovered = el && isScroller(el) ? el : null;
    if (hovered) show(hovered);
    else scheduleHide();
  });

  thumb.addEventListener("pointerenter", () => {
    overThumb = true;
    clearTimeout(hideTimer);
  });
  thumb.addEventListener("pointerleave", () => {
    overThumb = false;
    scheduleHide();
  });

  thumb.addEventListener("pointerdown", (e) => {
    const geo = layout();
    if (!geo || geo.travel <= 0) return;
    e.preventDefault(); // suppress the text selection a drag would otherwise start
    drag = { pointerY: e.clientY, scrollTop: target!.scrollTop, ...geo };
    thumb.dataset.dragging = "true";
    thumb.setPointerCapture(e.pointerId);
  });

  thumb.addEventListener("pointermove", (e) => {
    if (!drag) return;
    const moved = (e.clientY - drag.pointerY) / drag.travel;
    target!.scrollTop = drag.scrollTop + moved * drag.scrollRange;
  });

  const endDrag = (e: PointerEvent) => {
    if (!drag) return;
    drag = null;
    thumb.dataset.dragging = "false";
    thumb.releasePointerCapture(e.pointerId);
    scheduleHide();
  };
  thumb.addEventListener("pointerup", endDrag);
  thumb.addEventListener("pointercancel", endDrag);
}
